import React from "react";
import "./ScrollingText.scss";

const SCROLL_SPEED_PX_PER_SECOND = 45;
const MIN_SCROLL_DURATION_SECONDS = 1.2;
const MAX_SCROLL_DURATION_SECONDS = 8;

/**
 * Single line of text that fades out at its right edge when it overflows and
 * scrolls itself fully into view on hover. Text that already fits gets neither
 * the fade nor the hover animation.
 */
export default function ScrollingText({ text, className = "" }) {
  const containerRef = React.useRef(null);
  const textRef = React.useRef(null);
  const [overflowWidth, setOverflowWidth] = React.useState(0);

  const measure = React.useCallback(() => {
    const container = containerRef.current;
    const textNode = textRef.current;
    if (!container || !textNode) return;

    const overflow = textNode.scrollWidth - container.clientWidth;
    setOverflowWidth(overflow > 1 ? overflow : 0);
  }, []);

  React.useEffect(() => {
    measure();

    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure, text]);

  const isOverflowing = overflowWidth > 0;
  const duration = Math.min(
    MAX_SCROLL_DURATION_SECONDS,
    Math.max(MIN_SCROLL_DURATION_SECONDS, overflowWidth / SCROLL_SPEED_PX_PER_SECOND),
  );

  return (
    <span
      ref={containerRef}
      className={[
        "scrolling-text",
        isOverflowing ? "scrolling-text--overflowing" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      title={isOverflowing ? text : undefined}
      onMouseEnter={measure}
      style={
        isOverflowing
          ? {
              "--scrolling-text-distance": `-${overflowWidth}px`,
              "--scrolling-text-duration": `${duration.toFixed(2)}s`,
            }
          : undefined
      }
    >
      <span ref={textRef} className="scrolling-text__inner">
        {text}
      </span>
    </span>
  );
}
